import { FC, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';
import { FormattedPrice } from '../FormattedPrice';

import { useItemPriceData } from './hooks/useItemPriceData';

import { ItemPrice_item$key } from './__generated__/ItemPrice_item.graphql';
import { ItemPrice_displayPrice$key } from './__generated__/ItemPrice_displayPrice.graphql';

import dibsCss from 'dibs-css';
import styles from './ItemPrice.scss';

const itemFragment = graphql`
    fragment ItemPrice_item on Item {
        ...useItemPriceData_item
    }
`;

const displayPriceFragment = graphql`
    fragment ItemPrice_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...useItemPriceData_displayPrice
    }
`;

type PriceData = NonNullable<ReturnType<typeof useItemPriceData>>;

const CurrentPrice: FC<{
    currentPrice: PriceData['currentPrice'];
    quantityLabel: PriceData['quantityLabel'];
    currency: string;
}> = ({ currentPrice, currency, quantityLabel }) => {
    if (!currentPrice.amount) {
        return null;
    }

    return (
        <span className={classnames(dibsCss.inlineBlock, dibsCss.mrXsmall)}>
            <span
                className={classnames(dibsCss.sassyFontBodyLargeHeavy, {
                    [dibsCss.textAlertsecondary]: currentPrice.type === 'WEEKLY_SALE',
                })}
            >
                <FormattedPrice value={currentPrice.amount} currency={currency} />
            </span>
            {quantityLabel && (
                <span
                    className={classnames(
                        dibsCss.inlineBlock,
                        dibsCss.sassyFontBodySmall,
                        dibsCss.alignTextBottom,
                        dibsCss.mlXxsmall
                    )}
                >
                    {quantityLabel}
                </span>
            )}
        </span>
    );
};

const CurrentPriceLabelAndPercentageOff: FC<{
    type: PriceData['currentPrice']['type'];
    percentageOff: PriceData['percentageOff'];
}> = ({ type, percentageOff }) => {
    const showPriceLabel = ['NET', 'PRIVATE_OFFER'].includes(type);
    const showPercentageOff = !!percentageOff; // we want to show percentage off only if it's not falsy (including 0)

    if (!showPriceLabel && !showPercentageOff) {
        return null;
    }

    return (
        <span
            className={classnames(dibsCss.sassyFontBodySmallHeavy, dibsCss.alignTextBottom, {
                [dibsCss.textAlertsecondary]:
                    ['WEEKLY_SALE', 'PRIVATE_OFFER'].includes(type) && showPercentageOff,
                [dibsCss.textSuccesssecondary]: type === 'NET',
            })}
        >
            <FormattedMessage
                id="ItemPrice.priceLabel"
                defaultMessage="{type, select, NET {Net Price} PRIVATE_OFFER {Private Offer} other {}}{showDivider, select, true { | } other {}}{showPercentageOff, select, true {{percentageOff}% Off} other {}}"
                values={{
                    type,
                    showDivider: showPriceLabel && showPercentageOff,
                    showPercentageOff,
                    percentageOff,
                }}
            />
        </span>
    );
};

export const ItemPrice: FC<{
    item?: ItemPrice_item$key | null;
    displayPrice?: ItemPrice_displayPrice$key | null;
    currency: string;
    priceNote?: ReactNode;
}> = ({ item: itemRef, displayPrice: displayPriceRef, currency, priceNote }) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);

    const data = useItemPriceData({ item, displayPrice, currency });

    if (!data) {
        return null;
    }

    const {
        currentPrice,
        originalPrice,
        quantityLabel,
        percentageOff,
        vatIncluded,
        priceBookName,
    } = data;

    if (currentPrice.type === 'PRICE_UPON_REQUEST') {
        return (
            <div className={classnames(dibsCss.sassyFontBodyLarge, dibsCss.textNoir)}>
                <FormattedMessage
                    id="ItemPrice.priceUponRequest"
                    defaultMessage="Price Upon Request"
                />
            </div>
        );
    }

    const showPriceNote = !!priceNote;

    return (
        <div className={dibsCss.textNoir}>
            {originalPrice?.amount && (
                <span
                    className={classnames(
                        styles.originalPrice,
                        dibsCss.sassyFontBodyLarge,
                        dibsCss.textCharcoal,
                        dibsCss.inlineBlock,
                        dibsCss.mrXxsmall
                    )}
                >
                    <FormattedPrice value={originalPrice.amount} currency={currency} />
                </span>
            )}

            <CurrentPrice
                currentPrice={currentPrice}
                quantityLabel={quantityLabel}
                currency={currency}
            />

            <CurrentPriceLabelAndPercentageOff
                type={currentPrice.type}
                percentageOff={percentageOff}
            />

            {(vatIncluded || showPriceNote) && (
                <span
                    className={classnames(dibsCss.sassyFontBodySmall, dibsCss.alignTextBottom, {
                        [dibsCss.block]: currentPrice.type !== 'RETAIL',
                    })}
                >
                    <FormattedMessage
                        id="ItemPrice.priceNote"
                        defaultMessage="{vatIncluded, select, true {{vatLabel}} other {}}{showDivider, select, true { | } other {}}{showPriceNote, select, true {{priceNote}} other {}}"
                        values={{
                            vatIncluded,
                            vatLabel: vatMessages.included({ priceBookName }),
                            showDivider: vatIncluded && showPriceNote,
                            showPriceNote,
                            priceNote,
                        }}
                    />
                    {}
                </span>
            )}
        </div>
    );
};
